import {
  CircleCheck,
  CircleX,
  FileText,
  type LucideIcon,
  Minus,
  Settings2,
  ShieldCheck,
  Wrench,
} from 'lucide-react'
import type {
  SiteHealthEvaluationState,
  SiteHealthLevel,
  SiteHealthSeverity,
} from '@/lib/siteHealth.types'
import type {
  SiteHealthActionTypeIconMatch,
  SiteHealthEvaluationMetadata,
  SiteHealthEvaluationStyleMap,
  SiteHealthSeverityStyleMap,
} from './SiteHealthStyles.types'

export const EYEBROW_CLASS =
  'text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'
export const CARD_CLASS = 'rounded-2xl border border-border bg-card shadow-sm'
export const MESSAGE_CLASS =
  'rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground'
export const ERROR_MESSAGE_CLASS =
  'rounded-2xl border border-red-200 bg-red-50 p-10 text-center text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300'
export const PARTIAL_BADGE_CLASS =
  'inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300'
const SEVERITY_STYLES: SiteHealthSeverityStyleMap = {
  critical: {
    badge:
      'border-red-200 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
    bar: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
  },
  warning: {
    badge:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
    bar: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
  },
  info: {
    badge: 'border-border bg-muted text-muted-foreground',
    bar: 'bg-slate-400 dark:bg-slate-500',
    text: 'text-muted-foreground',
  },
}
const EVALUATION_STYLES: SiteHealthEvaluationStyleMap = {
  passed: {
    icon: CircleCheck,
    className: 'text-emerald-600 dark:text-emerald-400',
    label: 'Passed',
  },
  finding: {
    icon: CircleX,
    className: 'text-red-500 dark:text-red-400',
    label: 'Finding',
  },
  not_evaluated: {
    icon: Minus,
    className: 'text-muted-foreground',
    label: 'Not evaluated',
  },
}
const ACTION_TYPE_ICONS: readonly SiteHealthActionTypeIconMatch[] = [
  { match: 'access', icon: ShieldCheck },
  { match: 'robot', icon: ShieldCheck },
  { match: 'content', icon: FileText },
  { match: 'schema', icon: FileText },
  { match: 'config', icon: Settings2 },
  { match: 'render', icon: Wrench },
  { match: 'code', icon: Wrench },
]

export function severityBadgeClass(severity: SiteHealthSeverity): string {
  return `inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLES[severity].badge}`
}

export function severityBarClass(severity: SiteHealthSeverity): string {
  return SEVERITY_STYLES[severity].bar
}

export function severityTextClass(severity: SiteHealthSeverity): string {
  return SEVERITY_STYLES[severity].text
}

export function actionTypeIcon(
  actionType: string | null | undefined
): LucideIcon {
  const normalized = actionType?.toLocaleLowerCase() ?? ''

  return (
    ACTION_TYPE_ICONS.find(({ match }) => normalized.includes(match))?.icon ??
    Wrench
  )
}

export function evaluationIcon(
  state: SiteHealthEvaluationState | undefined
): SiteHealthEvaluationMetadata {
  return EVALUATION_STYLES[state ?? 'finding']
}

export function scoreTone(score: number): string {
  if (score >= 70) return 'text-emerald-600 dark:text-emerald-400'

  if (score >= 40) return 'text-amber-700 dark:text-amber-400'

  return 'text-red-600 dark:text-red-400'
}

const EFFORT_BADGES: Record<SiteHealthLevel, string> = {
  low: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  medium:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
  high: 'border-red-200 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
}
const EFFORT_LABELS: Record<SiteHealthLevel, string> = {
  low: 'Easy',
  medium: 'Medium',
  high: 'Hard',
}
const IMPACT_LABELS: Record<SiteHealthLevel, string> = {
  low: 'Low',
  medium: 'Med',
  high: 'High',
}

export function effortBadgeClass(level: SiteHealthLevel): string {
  return `inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${EFFORT_BADGES[level]}`
}

export function effortLabel(level: SiteHealthLevel): string {
  return EFFORT_LABELS[level]
}

export function impactLabel(level: SiteHealthLevel): string {
  return IMPACT_LABELS[level]
}
